import SettingsIcon from "@mui/icons-material/Settings";
import { Box, IconButton, Typography } from "@mui/material";
import React from "react";
import { useNavigate } from "react-router-dom";
import {
    mono,
    getSlidingSafeAreaTop,
    slidingBottomNavClearance,
    slidingPageColumnSx,
} from "../styles/mainStyles";
import { useSlidingAppearance } from "../lib/AppearanceContext";
import PageHeroSection from "./PageHeroSection";
import { SETTINGS_PATH, SLIDING_PUZZLE_MASTER_TITLE } from "../routes";
import { getHomePremium } from "../styles/homePremiumStyles";

/** Shared layout: title, optional settings, scrollable body (bottom nav lives in Layout). */
export default function PageShell({
    children,
    scrollRef,
    showSettings,
    title,
    /** When false, omit the centered brand bar (e.g. Library uses a large in-page title). */
    showBrandHeader = true,
    /** Left-aligned screen title with settings on the right (e.g. "MY PUZZLES"). */
    pageTitle = null,
    /** Home-style screen: content height only (no flex grow / gap above tab bar). */
    compact = false,
    /** Gradient hero: "home" | "library" | "myPuzzles" | "settings" (light mode). */
    pageHero = null,
    /** Optional content inside the hero band (e.g. Resume Last Game card). */
    heroChildren = null,
    /** Optional element before the page title in the hero (e.g. back chevron). */
    leading = null,
    /** When false, skip the shared content column (e.g. Settings grouped list bleed). */
    contentColumn = true,
}) {
    const navigate = useNavigate();
    const { tokens, isDark } = useSlidingAppearance();
    const home = getHomePremium(isDark);
    const heading = title ?? SLIDING_PUZZLE_MASTER_TITLE.toUpperCase();
    const useInlinePageTitle = Boolean(pageTitle) && !showBrandHeader && !pageHero;
    const safeAreaTop = getSlidingSafeAreaTop();
    const scrollMaxHeight = compact
        ? undefined
        : `calc(100dvh - ${safeAreaTop} - ${slidingBottomNavClearance} - 3.25rem)`;
    const useContentColumn = Boolean(pageHero) && contentColumn;

    return (
        <Box
            sx={{
                display: "flex",
                flexDirection: "column",
                px: pageHero ? 0 : 1,
                pt: pageHero ? 0 : 0.5,
                pb: 0,
                fontFamily: mono,
                boxSizing: "border-box",
                flex: 1,
                minHeight: 0,
                bgcolor: "transparent",
                color: pageHero ? home.text : tokens.text,
            }}
        >
            <Box
                sx={{
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    width: "100%",
                    maxWidth: "100%",
                    mx: 0,
                    minHeight: 0,
                }}
            >
                {pageHero ? (
                    <PageHeroSection
                        variant={pageHero}
                        title={pageTitle || heading}
                        showSettings={showSettings}
                        leading={leading}
                    >
                        {heroChildren}
                    </PageHeroSection>
                ) : null}
                {!pageHero && (showBrandHeader || showSettings || useInlinePageTitle) ? (
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: useInlinePageTitle
                                ? "space-between"
                                : showBrandHeader
                                  ? "center"
                                  : "flex-end",
                            position: "relative",
                            mb: showBrandHeader || useInlinePageTitle ? 1 : 0.75,
                            flexShrink: 0,
                            minHeight: 44,
                            gap: 1,
                        }}
                    >
                        {useInlinePageTitle ? (
                            <Typography
                                variant="h5"
                                component="h1"
                                sx={{
                                    fontFamily:
                                        '-apple-system, BlinkMacSystemFont, "SF Pro Display", "Segoe UI", sans-serif',
                                    fontWeight: 700,
                                    textAlign: "left",
                                    letterSpacing: "0.04em",
                                    fontSize: "0.9375rem",
                                    textTransform: "uppercase",
                                    color: tokens.text,
                                    flex: 1,
                                    minWidth: 0,
                                }}
                            >
                                {pageTitle}
                            </Typography>
                        ) : null}
                        {showBrandHeader ? (
                            <Typography
                                variant="h5"
                                component="h1"
                                sx={{
                                    fontFamily:
                                        '-apple-system, BlinkMacSystemFont, "SF Pro Display", "Segoe UI", sans-serif',
                                    fontWeight: 700,
                                    textAlign: "center",
                                    letterSpacing: "0.04em",
                                    fontSize: "0.9375rem",
                                    textTransform: "uppercase",
                                    color: tokens.text,
                                    px: showSettings ? 5 : 0,
                                }}
                            >
                                {heading}
                            </Typography>
                        ) : null}
                        {showSettings ? (
                            <IconButton
                                type="button"
                                aria-label="Settings"
                                onClick={() => navigate(SETTINGS_PATH)}
                                sx={{
                                    position:
                                        showBrandHeader && !useInlinePageTitle ? "absolute" : "relative",
                                    right: showBrandHeader && !useInlinePageTitle ? 0 : undefined,
                                    top: showBrandHeader && !useInlinePageTitle ? "50%" : undefined,
                                    transform:
                                        showBrandHeader && !useInlinePageTitle
                                            ? "translateY(-50%)"
                                            : undefined,
                                    flexShrink: 0,
                                    color: tokens.text,
                                    minWidth: 44,
                                    minHeight: 44,
                                }}
                            >
                                <SettingsIcon />
                            </IconButton>
                        ) : null}
                    </Box>
                ) : null}
                <Box
                    ref={scrollRef}
                    sx={{
                        flex: 1,
                        minHeight: 0,
                        maxHeight: scrollMaxHeight,
                        overflowY: compact ? "visible" : "auto",
                        WebkitOverflowScrolling: "touch",
                        display: compact ? "flex" : "block",
                        flexDirection: compact ? "column" : undefined,
                    }}
                >
                    {useContentColumn ? (
                        <Box
                            sx={{
                                ...slidingPageColumnSx,
                                ...(compact
                                    ? {
                                          flex: 1,
                                          minHeight: 0,
                                          display: "flex",
                                          flexDirection: "column",
                                      }
                                    : null),
                            }}
                        >
                            {children}
                        </Box>
                    ) : (
                        children
                    )}
                </Box>
            </Box>
        </Box>
    );
}
